import values from 'lodash/values';
import {
    createDestination,
    createDestinationConfigurations,
    createExport,
    deleteExportAsSuper,
    deleteDestination,
    fetchDestination,
    fetchDestinations,
    fetchExport,
    fetchExportEstimate,
    fetchExports,
    fetchExportsByTypeAsSuper,
    updateDestination,
    updateExport,
    validateDestination,
} from './data-sync.utils';
import { createFrozenMap } from '@/vuex/utils';

let loadAllPromise;

const dataSync = {
    namespaced: true,
    state: {
        destinationMap: {},
        destinationConfigurationsMap: {},
        error: {},
        exportMap: {},
        exportMapVersion: 0,
        isExportsLoading: false,
        isInitializing: false,
        hasExports: false,
    },
    mutations: {
        deleteExportMapAtKey (state, { id }) {
            delete state.exportMap[id];
        },
        setDestinationMap (state, { map }) {
            state.destinationMap = map;
        },
        setDestinationMapAtKey (state, { id, destination }) {
            state.destinationMap[id] = destination;
        },
        setDestinationConfigurationsMapAtKey (state, { type, destinationConfigurations }) {
            state.destinationConfigurationsMap[type] = destinationConfigurations;
        },
        setError (state, { error }) {
            state.error = error;
        },
        setExportMap (state, { map }) {
            state.exportMap = map;
        },
        setExportMapAtKey (state, { id, exportObj }) {
            state.exportMap[id] = exportObj;
            state.exportMapVersion += 1;
        },
        setIsInitializing (state, value) {
            state.isInitializing = value;
        },
        setIsExportsLoading (state, value) {
            state.isExportsLoading = value;
        },
        setHasExports (state, value) {
            state.hasExports = value;
        },
        deleteDestinationMapAtKey (state, { id }) {
            delete state.destinationMap[id];
        },
    },
    actions: {
        async loadAll ({ commit }, { reFetch = false } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            commit('setIsInitializing', true);

            loadAllPromise = Promise.all([
                fetchDestinations(),
                fetchExports({ dataType: 'events' }),
                fetchExports({ dataType: 'accountsAndVisitors' }),
            ]);

            try {
                const [
                    { data: destinations },
                    { data: eventsExports },
                    { data: accountsAndVisitorsExports },
                ] = await loadAllPromise;
                const rawExports = [...eventsExports, ...accountsAndVisitorsExports];
                const exports = rawExports.filter((item) => item.type !== 'Archived');
                const hasExports = exports.length > 0;

                commit('setIsInitializing', false);
                commit('setDestinationMap', {
                    map: createFrozenMap(destinations),
                });
                commit('setExportMap', {
                    map: createFrozenMap(exports),
                });
                commit('setHasExports', hasExports);
            } catch (error) {
                commit('setIsInitializing', false);
                commit('setError', { error });
                throw error;
            }
        },
        async loadOneDestination ({ commit, getters }, { reFetch = false, id }) {
            if (!reFetch && getters.destinationById(id)) return Promise.resolve();

            try {
                const { data: destination } = await fetchDestination(id);

                commit('setDestinationMapAtKey', {
                    id: destination.id,
                    destination: Object.freeze(destination),
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async loadOneExport ({ commit, getters }, { reFetch = false, id }) {
            if (!reFetch && getters.exportById(id)) return Promise.resolve();

            try {
                const { data: exportObj } = await fetchExport(id);

                commit('setExportMapAtKey', {
                    id: exportObj.id,
                    exportObj: Object.freeze(exportObj),
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async loadTestExportAsSuper ({ commit }, { subId }) {
            try {
                const { data: testExports } = await fetchExportsByTypeAsSuper(subId, 'test');
                testExports.forEach((exportObj) => {
                    commit('setExportMapAtKey', {
                        id: exportObj.id,
                        exportObj: Object.freeze(exportObj),
                    });
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async createDestination ({ commit }, { destination }) {
            try {
                const { data: newDestination } = await createDestination(destination);
                commit('setDestinationMapAtKey', { id: newDestination.id, destination: newDestination });

                return newDestination;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async createDestinationConfigurations ({ commit }, { destination }) {
            try {
                const { data: newDestinationConfigurations } = await createDestinationConfigurations(destination);
                commit('setDestinationConfigurationsMapAtKey', {
                    type: newDestinationConfigurations.type,
                    destinationConfigurations: newDestinationConfigurations,
                });

                return newDestinationConfigurations;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async createExport ({ commit }, { exportObj }) {
            try {
                const { data: newExportObj } = await createExport(exportObj);
                commit('setExportMapAtKey', { id: newExportObj.id, exportObj: newExportObj });
                commit('setHasExports', true);

                return newExportObj;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async deleteExportAsSuper ({ commit }, { exportId, subId }) {
            try {
                await deleteExportAsSuper({ exportId, subId });

                commit('deleteExportMapAtKey', {
                    id: exportId,
                });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async getExportByDate ({ commit }, { dataType = 'events', start, end } = {}) {
            commit('setIsExportsLoading', true);
            try {
                const { data: exports } = await fetchExports({ dataType, start, end });
                const filteredExports = exports.filter((exportObj) => exportObj.type !== 'Archived');
                commit('setIsExportsLoading', false);
                commit('setExportMap', {
                    map: createFrozenMap(filteredExports),
                });
            } catch (error) {
                commit('setIsExportsLoading', false);
                commit('setError', { error });
                throw error;
            }
        },
        async getExportEstimate (context, { exportObj }) {
            try {
                const { data: estimate } = await fetchExportEstimate(exportObj);

                return estimate;
            } catch (error) {
                throw error;
            }
        },
        async updateDestination ({ commit }, { destination }) {
            try {
                const { data: updatedDestination } = await updateDestination(destination);
                commit('setDestinationMapAtKey', { id: destination.id, destination: updatedDestination });

                return updatedDestination;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateExport ({ commit, state }, { exportObj }) {
            try {
                const { data: updatedExportObj } = await updateExport(exportObj);

                // Retrieve the current export map entry to preserve existing values
                const currentExport = state.exportMap?.[exportObj.id] || {};

                // If healthStatus is an empty string, preserve the existing one
                const mergedExportObj = {
                    ...currentExport,
                    ...updatedExportObj,
                    ...(updatedExportObj.healthStatus === '' && { healthStatus: currentExport.healthStatus }),
                };

                commit('setExportMapAtKey', { id: exportObj.id, exportObj: mergedExportObj });

                return mergedExportObj;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async validateDestination (context, { destination }) {
            try {
                const { data: validatedDestination } = await validateDestination(destination);

                return !!validatedDestination.testFileDestination && validatedDestination.status === 'Validating';
            } catch (error) {
                throw error;
            }
        },
        async deleteDestination ({ commit }, id) {
            try {
                await deleteDestination(id);
                commit('deleteDestinationMapAtKey', { id });
            } catch (error) {
                throw error;
            }
        },
    },
    getters: {
        destinationById: (state) => (id) => state.destinationMap[id],
        destinations: (state) => values(state.destinationMap),
        error: (state) => state.error,
        exportById: (state) => (id) => state.exportMap[id],
        exports: (state) => values(state.exportMap),
        exportsByDataType: (state) => (dataType) =>
            values(state.exportMap).filter((item) => item.dataType === dataType),
        hasExports: (state) => state.hasExports,
        isExportsLoading: (state) => state.isExportsLoading,
        isInitializing: (state) => state.isInitializing,
        recurringExports: (state) => () => values(state.exportMap).filter((item) => item.type === 'Recurring'),
        recurringExportsByDataTypeAndDestination: (state) => (dataType, destinationId) =>
            values(state.exportMap).filter(
                (item) =>
                    item.type === 'Recurring' &&
                    item.dataType === dataType &&
                    (destinationId == null || item.destinationId === destinationId),
            ),
    },
};

export default dataSync;
